/**
 * Shared helper for logging a client-facing automated email send to
 * the `sent_emails` table so it surfaces in the therapist app's
 * client file → Sent Emails tab (viewer scopes by `episodeId`, so
 * callers should pass episodeId when available).
 *
 * This helper is best-effort — any error is logged + swallowed so a
 * logging miss never breaks the real email send path. Callers should
 * `void logClientSentEmail(...)` or `await` without a try/catch; the
 * helper owns its own error isolation.
 */

import type { SupabaseClient } from '@supabase/supabase-js'
import { randomUUID } from 'crypto'

export interface LogClientSentEmailArgs {
  /** Row in email_templates (looked up by name). Create a migration
   *  seeding the row before enabling logging for a new template. */
  templateName: string
  /** The client receiving the email. Null for referral chases etc.
   *  where there is no users row — scope by referralId instead. */
  userId: string | null
  /** The episode the email relates to. Required for the row to show
   *  up in the therapist's client-file Sent Emails tab (the viewer
   *  filters on episodeId). */
  episodeId?: string | null
  /** Session the email relates to, when applicable (reminders,
   *  cancellations, no-shows). */
  sessionId?: string | null
  /** Group the email relates to (couples/group therapy flows). */
  groupId?: string | null
  /** BES referral tracking. */
  referralId?: string | null
  /** Snapshot of the vars used to render the email — surfaces in the
   *  Email History UI so the therapist can see what the client got. */
  templateVars?: Record<string, unknown>
  /** The pro who triggered the send, if human-initiated. Null for
   *  cron / system sends. */
  sentBy?: string | null
}

export async function logClientSentEmail(
  admin: SupabaseClient,
  args: LogClientSentEmailArgs,
): Promise<{ logged: boolean; error?: string }> {
  try {
    const { data: template } = await admin
      .from('email_templates')
      .select('id')
      .eq('name', args.templateName)
      .eq('isActive', true)
      .maybeSingle()

    if (!template?.id) {
      console.warn(
        `[sent_emails] template "${args.templateName}" missing or inactive — skipping log`,
      )
      return { logged: false, error: 'template-missing' }
    }

    const now = new Date().toISOString()
    const { error } = await admin.from('sent_emails').insert({
      id: randomUUID(),
      templateId: template.id,
      templateVars: args.templateVars ?? {},
      userId: args.userId,
      sentBy: args.sentBy ?? null,
      episodeId: args.episodeId ?? null,
      sessionId: args.sessionId ?? null,
      groupId: args.groupId ?? null,
      referralId: args.referralId ?? null,
      status: 'SENT',
      createdAt: now,
      updatedAt: now,
    })

    if (error) {
      console.warn(`[sent_emails] insert failed for "${args.templateName}":`, error.message)
      return { logged: false, error: error.message }
    }

    return { logged: true }
  } catch (err: any) {
    console.warn(`[sent_emails] unexpected error logging "${args.templateName}":`, err?.message)
    return { logged: false, error: err?.message || 'unknown' }
  }
}
